import { useCallback } from "react";
import { ScenarioModel } from "../../clients/KompasApi";
import {
  Collapse,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  highlightDifferencesAtom,
  onlyShowDifferencesAtom,
} from "../../pages/ComparePage";
import {
  extractValue,
  hasAnyMeasure,
  hasMeasure,
} from "../../shared/utils/measureUtils";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryFooter } from "./CategoryFooter";
import { FixedNumberDisplay } from "../../elements/NumberDisplay";
import { TaxKey } from "../TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const tempCalculateCO2Impact = (scenario: ScenarioModel) =>
  scenario.calculation.yearlyCO2Emission /
  ((scenario.facadeInsulationThermalResistance ?? 0) +
    (scenario.panelInfillThermalResistance ?? 0) || 1);
export const tempCalculatePrice = (scenario: ScenarioModel) => 
  ((scenario.facadeInsulationThermalResistance ?? 0) +
    (scenario.panelInfillThermalResistance ?? 0)) *
  1000;

export function FacadeMeasuresSubTable() {
  const { scenarios, situation } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const [onlyShowDifferences] = useAtom(onlyShowDifferencesAtom);
  const [highlightDifferences] = useAtom(highlightDifferencesAtom);

  const scenarioValuesDiffer = useCallback(
    (measure: keyof ScenarioModel) =>
      new Set(scenarios.map((scenario) => extractValue(scenario[measure])))
        .size > 1,
    [scenarios],
  );

  return (
    <Collapse
      in={hasAnyMeasure(
        [
          "facadeInsulation",
          "facadeInsulationThermalResistance",
          "gableEndInsulation",
          "gableEndInsulationThermalResistance",
        ],
        scenarios,
        situation,
        onlyShowDifferences,
      )}
    >
      <Table
        className={isOpen ? "open" : "closed"}
        sx={{ borderSpacing: "2rem 0" }}
        variant="compare"
      >
        <CategoryHeader
          isOpen={isOpen}
          onToggle={onToggle}
        >
          Gevel
        </CategoryHeader>
        <Tbody>
          {hasMeasure(
            "facadeInsulationThermalResistance",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("facadeInsulationThermalResistance")
                  ? "highlight"
                  : ""
              }
            >
              <Td>Gevelisolatie RC waarde</Td>
              <Td textAlign="center">
                <FixedNumberDisplay
                  decimals={2}
                  number={situation.facadeInsulationThermalResistance ?? 0}
                />
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  <FixedNumberDisplay
                    decimals={2}
                    number={scenario.facadeInsulationThermalResistance ?? 0}
                  />
                </Td>
              ))}
            </Tr>
          )}
          {situation.relativePosition.name.includes("Hoekwoning") &&
            hasMeasure(
              "gableEndInsulationThermalResistance",
              scenarios,
              situation,
              onlyShowDifferences,
            ) && (
              <Tr
                className={
                  highlightDifferences &&
                  scenarioValuesDiffer("gableEndInsulationThermalResistance")
                    ? "highlight"
                    : ""
                }
              >
                <Td>Kopgevelisolatie RC waarde</Td>
                <Td textAlign="center">
                  <FixedNumberDisplay
                    decimals={2}
                    number={situation.gableEndInsulationThermalResistance ?? 0}
                  />
                </Td>
                {scenarios.map((scenario) => (
                  <Td key={scenario.id} textAlign="center">
                    <FixedNumberDisplay
                      decimals={2}
                      number={scenario.gableEndInsulationThermalResistance ?? 0}
                    />
                  </Td>
                ))}
              </Tr>
            )}
          {hasMeasure(
            "panelInfillThermalResistance",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("panelInfillThermalResistance")
                  ? "highlight"
                  : ""
              }
            >
              <Td>Paneelvulling RC waarde</Td>
              <Td textAlign="center">
                <FixedNumberDisplay
                  decimals={2}
                  number={situation.panelInfillThermalResistance ?? 0}
                />
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  <FixedNumberDisplay
                    decimals={2}
                    number={scenario.panelInfillThermalResistance ?? 0}
                  />
                </Td>
              ))}
            </Tr>
          )}
        </Tbody>
        <CategoryFooter
          calculateCO2Impact={tempCalculateCO2Impact}
          calculatePrice={tempCalculatePrice}
          taxKey={TaxKey.Facade}
        />
      </Table>
    </Collapse>
  );
}
